import { CLOAK_COLOURS } from "./look.js";
/**
 * SILHOUETTES — every guest without a selfie is a shadow portrait: a head and
 * shoulders in black against a glow of their colour, like an old cut-paper
 * profile. Sixteen shapes (hair, hats, a beard, a headscarf), one per crest,
 * so each guest is recognisable at a glance and nobody is a cartoon animal.
 *
 * Shapes in one colour merge, so each style only adds to the outline.
 * viewBox 0 0 100 120: head centred at (50, 52).
 */
const HEAD = `<ellipse cx="50" cy="52" rx="16.5" ry="20.5"/><path d="M43 66 h14 v22 h-14z"/><path d="M8 124 C10 96 28 86 50 86 C72 86 90 96 92 124 Z"/>`;
const STYLES = {
    // Short crop.
    fox: `<path d="M33 52 C31 30 69 30 67 52 C64 40 36 40 33 52Z"/>`,
    // Long, straight to the shoulders.
    raven: `<path d="M32 52 C30 26 70 26 68 52 L71 90 L29 90 Z"/>`,
    // A bun on top.
    stag: `<circle cx="50" cy="27" r="9"/><path d="M34 50 C33 32 67 32 66 50 C63 40 37 40 34 50Z"/>`,
    // Big curls.
    owl: `<circle cx="50" cy="44" r="25"/><circle cx="30" cy="56" r="9"/><circle cx="70" cy="56" r="9"/>`,
    // A flat cap.
    wolf: `<ellipse cx="50" cy="37" rx="19" ry="10"/><path d="M31 40 L80 37 L79 43 L31 46Z"/>`,
    // A bob.
    hare: `<path d="M31 54 C29 28 71 28 69 54 L70 70 C64 72 61 70 60 64 L40 64 C39 70 36 72 30 70 Z"/>`,
    // A ponytail.
    boar: `<path d="M34 50 C33 30 67 30 66 50 C63 39 37 39 34 50Z"/><path d="M64 38 C82 42 84 66 74 78 C76 62 71 52 62 46Z"/>`,
    // A beard.
    serpent: `<ellipse cx="50" cy="66" rx="14" ry="11"/><path d="M34 48 C33 32 67 32 66 48 C62 40 38 40 34 48Z"/>`,
    // A fedora.
    falcon: `<ellipse cx="50" cy="37" rx="29" ry="5"/><path d="M35 37 L37 21 C45 16 55 16 63 21 L65 37Z"/>`,
    // A top hat.
    badger: `<ellipse cx="50" cy="34" rx="24" ry="4.5"/><path d="M37 34 L38 8 L62 8 L63 34Z"/>`,
    // A quiff.
    moth: `<path d="M33 46 C32 22 58 16 72 28 C63 27 60 31 67 42 Z"/>`,
    // A headscarf.
    heron: `<path d="M30 56 C27 26 73 26 70 56 L75 84 C64 77 36 77 25 84 Z"/>`,
    // Spiky.
    bear: `<path d="M33 46 L35 27 L42 35 L46 21 L52 33 L58 21 L61 35 L67 27 L67 46Z"/>`,
    // Close-shaved, ears showing.
    lynx: `<ellipse cx="33.5" cy="54" rx="3.5" ry="6"/><ellipse cx="66.5" cy="54" rx="3.5" ry="6"/>`,
    // Pigtails.
    crow: `<path d="M34 50 C33 31 67 31 66 50 C63 39 37 39 34 50Z"/><circle cx="29" cy="58" r="8"/><circle cx="71" cy="58" r="8"/>`,
    // Long waves.
    toad: `<path d="M32 52 C30 26 70 26 68 52 L70 88 L30 88 Z"/><circle cx="30" cy="68" r="6"/><circle cx="70" cy="68" r="6"/><circle cx="31" cy="82" r="6"/><circle cx="69" cy="82" r="6"/>`,
};
const cache = new Map();
/** A data: URL for this guest's silhouette, ready for an <img>. */
export function silhouetteUrl(sigil, cloak) {
    const key = `${sigil}:${cloak ?? ""}`;
    const hit = cache.get(key);
    if (hit)
        return hit;
    const glow = cloak ? CLOAK_COLOURS[cloak] : "#c8a35f";
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120" preserveAspectRatio="xMidYMid slice">
<defs><radialGradient id="g" cx="50%" cy="42%" r="62%"><stop offset="0" stop-color="#f3e6c8"/><stop offset="0.38" stop-color="${glow}"/><stop offset="1" stop-color="#0c0f0e"/></radialGradient>
<linearGradient id="s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#16181a"/><stop offset="1" stop-color="#050606"/></linearGradient></defs>
<rect width="100" height="120" fill="url(#g)"/>
<g fill="url(#s)">${STYLES[sigil] ?? ""}${HEAD}</g>
</svg>`;
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    cache.set(key, url);
    return url;
}
/** Names for the silhouette picker on the phone. */
export const SILHOUETTE_NAMES = {
    fox: "Short crop",
    raven: "Long hair",
    stag: "Top knot",
    owl: "Curls",
    wolf: "Flat cap",
    hare: "Bob",
    boar: "Ponytail",
    serpent: "Beard",
    falcon: "Fedora",
    badger: "Top hat",
    moth: "Quiff",
    heron: "Headscarf",
    bear: "Spikes",
    lynx: "Close shave",
    crow: "Pigtails",
    toad: "Waves",
};
//# sourceMappingURL=silhouette.js.map